import {
  FETCH_ER_UNITS_REQUEST,
  FETCH_ER_UNITS_SUCCESS,
  FETCH_ER_UNITS_FAILURE,
  FETCH_ER_FAB_TYPES_REQUEST,
  FETCH_ER_FAB_TYPES_SUCCESS,
  FETCH_ER_FAB_TYPES_FAILURE,
  FETCH_ER_WEIGHTS_REQUEST,
  FETCH_ER_WEIGHTS_SUCCESS,
  FETCH_ER_WEIGHTS_FAILURE,
  FETCH_ER_UNIT_FABRICATIONS_REQUEST,
  FETCH_ER_UNIT_FABRICATIONS_SUCCESS,
  FETCH_ER_UNIT_FABRICATIONS_FAILURE,
  SAVE_ER_TARGET_DATES_REQUEST,
  SAVE_ER_TARGET_DATES_SUCCESS,
  SAVE_ER_TARGET_DATES_FAILURE,
  FETCH_ER_DELAY_REASONS_REQUEST,
  FETCH_ER_DELAY_REASONS_SUCCESS,
  FETCH_ER_DELAY_REASONS_FAILURE,
  FETCH_ER_DAILY_REMARKS_REQUEST,
  FETCH_ER_DAILY_REMARKS_SUCCESS,
  FETCH_ER_DAILY_REMARKS_FAILURE,
  SAVE_ER_DAILY_REMARK_REQUEST,
  SAVE_ER_DAILY_REMARK_SUCCESS,
  SAVE_ER_DAILY_REMARK_FAILURE,
  FETCH_ER_REMARK_HISTORY_REQUEST,
  FETCH_ER_REMARK_HISTORY_SUCCESS,
  FETCH_ER_REMARK_HISTORY_FAILURE,
} from "../constants/ErectionMonitorConstants";

import ErectionMonitorService from "../service/ErectionMonitorService";

// Backend sends the error text in Result (e.g. StatusCode 409)
const apiMessage = (data) =>
  data?.Message ||
  (typeof data?.Result === "string" && data.Result) ||
  data?.message ||
  "";

// Remark list from the API answer: Data: [...] (or ResultSet / Data.Remarks / a plain array)
const remarkList = (body) => {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return [];
  for (const k of ["Data", "ResultSet", "data", "Remarks", "DailyRemarks"]) {
    if (Array.isArray(body[k])) return body[k];
  }
  if (body.Data && typeof body.Data === "object") return remarkList(body.Data);
  console.warn("GetDailyRemarks: no remark list found in the answer", body);
  return [];
};

const failed = (data) => (data?.StatusCode && data.StatusCode !== 200) || data?.IsSuccess === false;

// Units (Bottom/Side Unit ...)
export const fetchErUnits = () => async (dispatch) => {
  try {
    dispatch({ type: FETCH_ER_UNITS_REQUEST });
    const response = await ErectionMonitorService.GetUnits();
    dispatch({ type: FETCH_ER_UNITS_SUCCESS, payload: response.data.Data });
  } catch (error) {
    dispatch({ type: FETCH_ER_UNITS_FAILURE, payload: apiMessage(error.response?.data) || error.message || "Failed to fetch units" });
  }
};

// Fabrication types (rows of the table, SeqNo order)
export const fetchErFabTypes = () => async (dispatch) => {
  try {
    dispatch({ type: FETCH_ER_FAB_TYPES_REQUEST });
    const response = await ErectionMonitorService.GetFabTypes();
    dispatch({ type: FETCH_ER_FAB_TYPES_SUCCESS, payload: response.data.Data });
  } catch (error) {
    dispatch({ type: FETCH_ER_FAB_TYPES_FAILURE, payload: apiMessage(error.response?.data) || error.message || "Failed to fetch fabrication types" });
  }
};

// Contribution % for a unit
export const fetchErWeights = (unitId) => async (dispatch) => {
  try {
    dispatch({ type: FETCH_ER_WEIGHTS_REQUEST, payload: String(unitId) });
    const response = await ErectionMonitorService.GetWeights(unitId);
    dispatch({ type: FETCH_ER_WEIGHTS_SUCCESS, payload: { key: String(unitId), data: response.data.Data || [] } });
  } catch (error) {
    dispatch({
      type: FETCH_ER_WEIGHTS_FAILURE,
      payload: { key: String(unitId), message: apiMessage(error.response?.data) || error.message || "Failed to fetch contributions" },
    });
  }
};

// Saved target dates of an IWO + unit
export const fetchErUnitFabrications = (iwoNo, unitId) => async (dispatch) => {
  const key = `${iwoNo}|${unitId}`;
  try {
    dispatch({ type: FETCH_ER_UNIT_FABRICATIONS_REQUEST, payload: key });
    const response = await ErectionMonitorService.GetUnitFabrications(iwoNo, unitId);
    dispatch({ type: FETCH_ER_UNIT_FABRICATIONS_SUCCESS, payload: { key, data: response.data.Data || null } });
  } catch (error) {
    dispatch({
      type: FETCH_ER_UNIT_FABRICATIONS_FAILURE,
      payload: { key, message: apiMessage(error.response?.data) || error.message || "Failed to fetch target dates" },
    });
  }
};

// Save target dates: first time -> InsertTargetDates, after that -> UpdateTargetDates (changed rows only)
// returns { success, message }
export const saveErTargetDates = ({ isSaved, componentId, iwoNo, unitId, fabrications, userId }) => async (dispatch) => {
  try {
    dispatch({ type: SAVE_ER_TARGET_DATES_REQUEST });

    const response = isSaved
      ? await ErectionMonitorService.UpdateTargetDates({ componentId, iwoNo, unitId, updatedBy: userId, fabrications })
      : await ErectionMonitorService.InsertTargetDates({ iwoNo, unitId, createdBy: userId, fabrications });
    const data = response?.data;

    if (failed(data)) {
      const message = apiMessage(data) || "Failed to save target dates";
      dispatch({ type: SAVE_ER_TARGET_DATES_FAILURE, payload: message });
      return { success: false, message };
    }

    dispatch({ type: SAVE_ER_TARGET_DATES_SUCCESS });
    return { success: true, data };
  } catch (error) {
    const message = apiMessage(error.response?.data) || error.message || "Failed to save target dates";
    dispatch({ type: SAVE_ER_TARGET_DATES_FAILURE, payload: message });
    return { success: false, message };
  }
};

// CDL + DTS delay reasons for the erection daily remark
export const fetchErDelayReasons = () => async (dispatch) => {
  try {
    dispatch({ type: FETCH_ER_DELAY_REASONS_REQUEST });
    const response = await ErectionMonitorService.GetDelayReasons();
    const data = response.data.Data || {};
    dispatch({
      type: FETCH_ER_DELAY_REASONS_SUCCESS,
      payload: { cdl: data.CdlDelayReasons || [], dts: data.DtsDelayReasons || [] },
    });
  } catch (error) {
    dispatch({ type: FETCH_ER_DELAY_REASONS_FAILURE, payload: apiMessage(error.response?.data) || error.message || "Failed to fetch delay reasons" });
  }
};

// Remarks of one stage (unit fabrication): GetDailyRemarks?progressId=
export const fetchErDailyRemarks = (progressId) => async (dispatch) => {
  const key = String(progressId);
  try {
    dispatch({ type: FETCH_ER_DAILY_REMARKS_REQUEST, payload: key });
    const response = await ErectionMonitorService.GetDailyRemarks({ progressId });
    dispatch({ type: FETCH_ER_DAILY_REMARKS_SUCCESS, payload: { key, data: remarkList(response.data) } });
  } catch (error) {
    dispatch({
      type: FETCH_ER_DAILY_REMARKS_FAILURE,
      payload: { key, message: apiMessage(error.response?.data) || error.message || "Failed to fetch remarks" },
    });
  }
};

// Save a daily remark: isUpdate -> UpdateDailyRemark (changed fields), else InsertDailyRemark
// returns { success, message }
export const saveErDailyRemark = ({ isUpdate, payload }) => async (dispatch) => {
  try {
    dispatch({ type: SAVE_ER_DAILY_REMARK_REQUEST });

    const response = isUpdate
      ? await ErectionMonitorService.UpdateDailyRemark(payload)
      : await ErectionMonitorService.InsertDailyRemark(payload);
    const data = response?.data;

    if (failed(data)) {
      const message = apiMessage(data) || "Failed to save remark";
      dispatch({ type: SAVE_ER_DAILY_REMARK_FAILURE, payload: message });
      return { success: false, message };
    }

    dispatch({ type: SAVE_ER_DAILY_REMARK_SUCCESS });
    return { success: true, data };
  } catch (error) {
    const message = apiMessage(error.response?.data) || error.message || "Failed to save remark";
    dispatch({ type: SAVE_ER_DAILY_REMARK_FAILURE, payload: message });
    return { success: false, message };
  }
};

// Remark History: GetDailyRemarks?iwoNo= (all units of the IWO)
// (componentId / progressId still work for one unit / one stage)
export const fetchErRemarkHistory = ({ iwoNo, componentId, progressId }) => async (dispatch) => {
  const key = iwoNo ? `i${iwoNo}` : progressId ? `p${progressId}` : `c${componentId}`;
  try {
    dispatch({ type: FETCH_ER_REMARK_HISTORY_REQUEST, payload: key });
    const response = await ErectionMonitorService.GetDailyRemarks(
      iwoNo ? { iwoNo } : progressId ? { progressId } : { componentId }
    );
    dispatch({ type: FETCH_ER_REMARK_HISTORY_SUCCESS, payload: { key, data: remarkList(response.data) } });
  } catch (error) {
    dispatch({
      type: FETCH_ER_REMARK_HISTORY_FAILURE,
      payload: { key, message: apiMessage(error.response?.data) || error.message || "Failed to fetch remarks" },
    });
  }
};